import React, { useEffect, useRef } from 'react';
import '@atlaskit/css-reset';
import { useQueryLoader } from 'react-relay';
import ProjectBreadcrumb from '@atlassian/jira-breadcrumbs/src/common-breadcrumbs/project/index.tsx';
import { setMark } from '@atlassian/jira-common-performance/src/marks.tsx';
import { createAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import RedirectIfProjectArchived from '@atlassian/jira-redirect-archived-project/src/ui/index.tsx';
import { useRelayResource } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import type { viewBacklogCustomFiltersQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogCustomFiltersQuery.graphql';
import VERSION_FILTER_RELAY_QUERY, {
	type viewBacklogVersionFilterRelayQuery,
} from '@atlassian/jira-relay/src/__generated__/viewBacklogVersionFilterRelayQuery.graphql';
import { backlogSyncResource } from '@atlassian/jira-router-resources-next-gen-backlog/src/services/backlog-sync/index.tsx';
import { customFiltersResource } from '@atlassian/jira-router-resources-next-gen-backlog/src/services/custom-filters/index.tsx';
import type { PrefetchedDataGql } from '@atlassian/jira-software-backlog-load-agility-graphql-data/src/types.tsx';
import ResourceRefreshOnMount from '@atlassian/jira-software-resource-invalidator/src/ui/resource-refresh-on-mount/index.tsx';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/sub-product-updater/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { Redirect, useResource, usePathParam } from '@atlassian/react-resource-router';
import {
	BACKLOG_PREFETCHED_DATA_READY_MARK,
	USE_RESOURCE_READY_MARK,
	USE_RESOURCE_START_MARK,
} from '../../../../common/constants.tsx';
import { getBoardUrl, isBacklogDisabled, useOnce } from '../../../../common/utils.tsx';
import BacklogApp from './backlog-app/index.tsx';

export const BacklogMetaAPIConsumer = () => {
	const [projectKey] = usePathParam('projectKey');
	const [boardIdStr] = usePathParam('boardId');
	const boardId = Number(boardIdStr);

	const tenantContext = useTenantContext();

	useOnce(() => {
		setMark(USE_RESOURCE_START_MARK);
	});

	const backlogDataResource = useResource<PrefetchedDataGql>(backlogSyncResource);

	useOnce(() => {
		setMark(USE_RESOURCE_READY_MARK);
	});

	useOnce(
		() => {
			setMark(BACKLOG_PREFETCHED_DATA_READY_MARK);
		},
		() => !!backlogDataResource.data,
	);

	const response = useRelayResource<viewBacklogCustomFiltersQuery>(customFiltersResource);
	const customFiltersQueryRef = response.queryReference;

	const isCacheHitRef = useRef(backlogDataResource.data != null);
	const isCacheHit = isCacheHitRef.current;

	const { data, loading, error } = backlogDataResource;

	const projectKeyFromContext = data?.boardScope.projectLocation.key || '';
	const projectNameFromContext = data?.boardScope.projectLocation.name || '';
	const projectIdFromContext = data?.boardScope.projectLocation.id || '';

	const [versionFilterRelayQueryRef, loadVersionFilterRelayQuery] =
		useQueryLoader<viewBacklogVersionFilterRelayQuery>(VERSION_FILTER_RELAY_QUERY);

	useEffect(() => {
		// only run the query if projectId exists in context
		// we load the query client-side because:
		//  1. The field relies on projectId which isn't available from the URL path
		//  2. We don't want version data to block the critical page load
		if (
			loadVersionFilterRelayQuery != null &&
			projectIdFromContext != null &&
			projectIdFromContext.length > 0
		) {
			const jiraProjectId = createAri({
				resourceOwner: 'jira',
				cloudId: tenantContext.cloudId,
				resourceType: 'project',
				resourceId: `${projectIdFromContext}`,
			});

			loadVersionFilterRelayQuery({
				jiraProjectIds: [jiraProjectId],
				first: 500,
				after: null,
			});
		}
	}, [loadVersionFilterRelayQuery, projectIdFromContext, tenantContext.cloudId]);

	// redirect to the board only if the backlog is disabled, no redirect if timeout
	if (loading === false && isBacklogDisabled(data)) {
		const urlProjectKey = projectKey ?? projectKeyFromContext;
		const boardUrl = getBoardUrl(urlProjectKey, String(boardId));

		return <Redirect to={boardUrl} />;
	}

	const backlogEl = (
		<>
			<SubProductUpdater subProduct="software" />
			<BacklogApp
				rapidViewId={boardId}
				projectKey={projectKey ?? projectKeyFromContext}
				projectKeyFromContext={projectKeyFromContext}
				projectNameFromContext={projectNameFromContext}
				projectIdFromContext={projectIdFromContext}
				tenantContext={tenantContext}
				data={data}
				error={error}
				loading={loading}
				isCacheHit={isCacheHit}
				customFiltersQueryRef={customFiltersQueryRef}
				versionFilterRelayQueryRef={versionFilterRelayQueryRef}
			/>
			<ResourceRefreshOnMount resource={backlogDataResource} refreshOnExpired />
		</>
	);

	return (
		<ProjectBreadcrumb
			baseUrl={tenantContext.baseUrl}
			projectKey={projectKeyFromContext}
			projectName={projectNameFromContext}
			projectId={projectIdFromContext}
			isSimplifiedProject
		>
			{backlogEl}
			<RedirectIfProjectArchived projectKey={projectKey ?? projectKeyFromContext ?? null} />
		</ProjectBreadcrumb>
	);
};

export default BacklogMetaAPIConsumer;
